import type { ColumnDef } from "@tanstack/react-table";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { textVariants } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import {
  formatNotificationTimestamp,
  getSentPackChangeLabel,
} from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

/**
 * What this technician was sent, when it is not what they would be sent now.
 *
 * Shows the figure it moved FROM, because that is the one the technician is looking
 * at and the one they will quote when they ask. A badge saying only "changed" sends
 * the manager back to a previous send nobody kept a record of.
 */
const SentPackChange = ({
  recipient,
}: {
  recipient: PayrollStatementNotificationRecipient;
}) => {
  const sentPack = recipient.sent_pack;
  const label = getSentPackChangeLabel(sentPack);
  if (!sentPack || !label) return null;

  const sentAt = formatNotificationTimestamp(sentPack.last_sent_at);

  // "was $X" rather than a struck-through figure with an arrow. Three glyph groups
  // in a caption was more furniture than information, and a line through a currency
  // amount reads as cancelled rather than as superseded.
  const previously =
    sentPack.change_kind === "amount" && sentPack.sent_final_pay
      ? `was ${sentPack.sent_final_pay}`
      : sentPack.change_kind === "details"
        ? "same total"
        : null;

  return (
    <Tooltip>
      {/* A real button trigger, as in ApprovalCell one column over: natively
          focusable and announced as interactive, which a div with tabIndex is not.
          That requires the contents to be PHRASING content, so this is spans carrying
          textVariants rather than Stack and Text, both of which render
          block elements a <button> may not contain. Same reason, same shape as Face
          in approval-col.tsx. */}
      <TooltipTrigger className="cursor-default text-left">
        {/* Inline rather than a flex row: flex `gap` spaces the two parts visually
            but leaves no whitespace between them in the text content, so the cell
            announced as "Needs renotifywas $2,555.75". Inline text with a real space
            reads correctly and still wraps at that space, which is what this column
            needs — a clipped currency figure is worse than a second line. */}
        <span className={textVariants({ variant: "caption" })}>
          <span className="text-warning">{label}</span>
          {previously && (
            <span className="text-subtle tabular-nums"> {previously}</span>
          )}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {sentAt
          ? `Published ${sentAt}, before these figures changed. They already see the new figures; renotify to let them know.`
          : "Published before these figures changed. They already see the new figures; renotify to let them know."}
      </TooltipContent>
    </Tooltip>
  );
};

export const FinalPayCell = ({
  recipient,
  isBuilding,
}: {
  recipient: PayrollStatementNotificationRecipient;
  isBuilding: boolean;
}) => {
  if (isBuilding) {
    return (
      <Text variant="caption" tone="muted">
        Building…
      </Text>
    );
  }
  if (recipient.statement_projection_error) {
    return (
      <Text variant="caption" tone="destructive">
        Unavailable
      </Text>
    );
  }
  if ((recipient.statement_audit_issues?.length ?? 0) > 0) {
    return (
      <Text variant="caption" className="text-warning">
        Needs review
      </Text>
    );
  }

  return (
    <Stack align="start" gap={1}>
      {recipient.final_pay ? (
        <Text variant="label" className="tabular-nums">
          {recipient.final_pay}
        </Text>
      ) : (
        <Text variant="caption" tone="muted">
          -
        </Text>
      )}
      <SentPackChange recipient={recipient} />
    </Stack>
  );
};

export const FinalPayCol = ({
  isBuilding,
}: {
  isBuilding: boolean;
}): ColumnDef<PayrollStatementNotificationRecipient, unknown> => ({
  id: "final_pay",
  enableSorting: false,
  header: () => <span>Final pay</span>,
  cell: ({ row }) => (
    <FinalPayCell recipient={row.original} isBuilding={isBuilding} />
  ),
});
